import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { DashboardCard, useBreakpoints } from '@monorepo/nova-ui';
import { useDashboard } from '../../../../../contexts';
import Gauge from '../../assets/gauge.svg?react';
import {
  GaugeCenter,
  GaugeContainer,
  GaugeLabelsContainer,
  GaugeNeedle,
  ProfitTargetContainer,
  ProfitTargetTitle,
} from '../styled-components';

export const ProfitTargetCard: FC = () => {
  const { accountBalance, program } = useDashboard();
  const { isTabletOrMobile } = useBreakpoints();

  const { t } = useTranslation('keyMetrics');

  // Calculate profit target progress: (current P&L * 100) / Target
  const currentPnL = accountBalance?.profitAndLoss || 0;
  const profitTarget = program?.config?.target || 10000; // Default target if not set

  const calculatedValue =
    profitTarget > 0 ? (currentPnL * 100) / profitTarget : 0;

  const min = 0;
  const max = 100;
  const clamped = Math.max(min, Math.min(max, calculatedValue));
  const range = Math.max(1, max - min);
  const ratio = (clamped - min) / range; // 0..1
  const minAngle = -90; // left end of arc
  const maxAngle = 90; // right end of arc
  const angle = minAngle + ratio * (maxAngle - minAngle);

  const gaugeWidth = isTabletOrMobile ? 90 : 160;
  const gaugeHeight = isTabletOrMobile ? 90 : 100;

  return (
    <DashboardCard>
      <ProfitTargetContainer>
        <ProfitTargetTitle
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.text.second,
            fontSize: theme.typography.pxToRem(14),
          })}
        >
          {t('cards.profitTarget.title')}
        </ProfitTargetTitle>
        <GaugeContainer gaugeWidth={gaugeWidth} gaugeHeight={gaugeHeight}>
          <Gauge width={gaugeWidth} height={gaugeHeight} />
          <GaugeNeedle angle={angle} />
          <GaugeCenter />
          <GaugeLabelsContainer>
            <Typography
              variant="caption"
              sx={(theme) => ({
                textAlign: 'center',
                margin: '0 auto',
                fontSize: theme.typography.pxToRem(24),
                fontWeight: 500,
              })}
            >
              {`${calculatedValue.toFixed(1)}%`}
            </Typography>
          </GaugeLabelsContainer>
        </GaugeContainer>
      </ProfitTargetContainer>
    </DashboardCard>
  );
};
